import React, { useState } from 'react'
import { IContextMenuState, IUserObject } from 'types'
import api from 'helpers/api'
import { formatShortDateTime, safeToISOStringWithZ } from 'helpers/date'
import { useApplicationStore } from 'hooks/application'
import Badge from 'components/badge'
import Button from 'components/button'
import Popover, { popoverPlacement } from 'components/popover'
import { BLUE_BUTTON } from 'components/save-buttons'
import { InlineRow } from 'views/settings/components/settingsCard'

const POPOVER_WIDTH = 240
// The list's maximum height before it scrolls, and each person's line: the popover's height to place it by.
const LIST_MAX_HEIGHT = 280
const PERSON_HEIGHT = 40
const POPOVER_CHROME = 60

interface Person {
  name: string
  email: string
}

/**
 * A role named in a sentence ("team manager", "organisation admin"), underlined, opening a popover that lists
 * the people in it with their emails, as a webhook's "3 events" opens its events.
 */
const PeoplePopover: React.FC<{ id: string; trigger: string; title: string; label: string; people: Person[] }> = ({
  id,
  trigger,
  title,
  label,
  people
}) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  return (
    <>
      <Button
        internalType="transparent"
        aria-haspopup="dialog"
        aria-expanded={!!at}
        className="h-auto p-0 align-baseline text-[13px] font-normal text-body underline underline-offset-2 decoration-body/40 hover:bg-transparent hover:text-body hover:decoration-body"
        // The popover closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(
            at
              ? null
              : popoverPlacement(event.currentTarget.getBoundingClientRect(), {
                  align: 'start',
                  width: POPOVER_WIDTH,
                  // As tall as it will be: a long list scrolls, so it never grows past its cap.
                  height: Math.min(people.length * PERSON_HEIGHT, LIST_MAX_HEIGHT) + POPOVER_CHROME
                })
          )
        }
      >
        {trigger}
      </Button>
      <Popover id={id} at={at} onClose={() => setAt(null)} label={label} title={title} width={POPOVER_WIDTH}>
        <ul className="flex flex-col gap-2 overflow-auto" style={{ maxHeight: LIST_MAX_HEIGHT }}>
          {people.map((person) => (
            <li key={person.email} className="min-w-0 text-[13px] leading-5">
              <div className="truncate text-body">{person.name}</div>
              <div className="truncate text-xs leading-4 text-muted">{person.email}</div>
            </li>
          ))}
        </ul>
      </Popover>
    </>
  )
}

/** People in name order: the server's order changes from one load to the next. */
const byName = (people: Person[]) => [...people].sort((a, b) => a.name.localeCompare(b.name))

/** The managers of the account's team, with their emails. */
const managersOf = (me: IUserObject): Person[] => byName(me.team?.managers ?? [])

/**
 * The account's organisation, team and plan, as rows of the Account card. A paid seat in an organisation
 * comes from one of its teams: a pool of paid seats, assigned to people by the team's managers. Without one,
 * the organisation is the one the account belongs to: by its organisation role or, usually, its email domain.
 */
const AccountRows: React.FC<{ me: IUserObject }> = ({ me }) => {
  const paid = me.subscriptionLevel !== 0
  const team = me.team
  const organisation = team?.orgName ?? me.organisationMembership?.name

  return (
    <>
      {organisation && (
        <InlineRow label="Organisation">
          <span className="font-medium text-body">{organisation}</span>
        </InlineRow>
      )}
      {team && (
        <InlineRow label="Team">
          <span className="font-medium text-body">{team.teamName}</span>
        </InlineRow>
      )}
      <InlineRow
        label="Plan"
        description={
          paid
            ? 'A paid seat can create and configure hubs, documents and tables, including their columns, views, permissions and integrations.'
            : 'A free account can view data, comment and add rows, but cannot create or configure hubs, documents or tables.'
        }
      >
        {paid && me.isOnFreeTrial && (
          <span className="text-muted">
            Free trial: {me.freeTrialDaysRemaining} {me.freeTrialDaysRemaining === 1 ? 'day' : 'days'} left
          </span>
        )}
        <Badge tone={paid ? 'blue' : 'muted'} size="large">
          {paid ? 'Paid seat' : 'Free'}
        </Badge>
      </InlineRow>
    </>
  )
}

/**
 * The Account card's footer, for an account whose seat comes from an organisation's team: who manages the
 * account, and the team managers to contact, in a popover.
 */
export const AccountFooter: React.FC<{ me: IUserObject }> = ({ me }) => {
  const managers = managersOf(me)
  if (!me.team) return null
  return (
    <p className="flex-1 text-muted">
      This account is managed by {me.team.orgName}.{' '}
      {managers.length ? (
        <>
          For help with access, contact a{' '}
          <PeoplePopover
            id="team-managers"
            trigger="team manager"
            title="Team managers"
            label={`Managers of the ${me.team.teamName} team`}
            people={managers}
          />
          .
        </>
      ) : (
        'For help with access, contact a team manager.'
      )}
    </p>
  )
}

// Where upgrades go for an account outside any organisation, as the app's other upgrade prompts say.
const UPGRADE_EMAIL = 'mo@morta.io'

/**
 * The Account card's footer for a free account: an invitation to upgrade. A member of an organisation asks its
 * admins, who give its paid seats: Request Paid Seat emails them, and "organisation admin" lists them. Anyone
 * else contacts Morta.
 */
export const UpgradeFooter: React.FC<{ me: IUserObject }> = ({ me }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [requesting, setRequesting] = useState(false)
  // When the account asked, sent (in UTC, without a zone) only while it can't ask again: the button stays done.
  const [requestedAt, setRequestedAt] = useState(() =>
    me.paidSeatRequestedAt ? new Date(safeToISOStringWithZ(me.paidSeatRequestedAt)) : null
  )
  // The organisation, when it has admins to ask.
  const withAdmins = me.organisationMembership?.admins.length ? me.organisationMembership : null

  const request = async () => {
    setRequesting(true)
    try {
      await api.requestPaidSeat()
    } catch (error) {
      setRequesting(false)
      displayErrorMessage(error)
      return
    }
    setRequesting(false)
    setRequestedAt(new Date())
    setSnackbarMessage({ status: 'success', message: 'Your organisation admins were asked for a paid seat' })
  }

  return (
    <>
      <div className="flex-1">
        <div className="font-medium text-body">Upgrade to create and edit on Morta</div>
        <p className="mt-0.5 text-muted">
          {withAdmins ? (
            <>
              Ask an{' '}
              <PeoplePopover
                id="organisation-admins"
                trigger="organisation admin"
                title="Organisation admins"
                label={`Admins of ${withAdmins.name}`}
                people={byName(withAdmins.admins)}
              />{' '}
              of {withAdmins.name} for a paid seat.
            </>
          ) : (
            'Contact us to unlock a paid seat.'
          )}
        </p>
        {withAdmins && requestedAt && (
          <p className="mt-0.5 text-muted">Requested {formatShortDateTime(requestedAt.toISOString())}.</p>
        )}
      </div>
      {withAdmins ? (
        <Button
          internalType="outline"
          className={BLUE_BUTTON}
          isLoading={requesting}
          disabled={Boolean(requestedAt)}
          onClick={request}
        >
          {requestedAt ? 'Paid Seat Requested' : 'Request Paid Seat'}
        </Button>
      ) : (
        <Button
          internalType="outline"
          className={BLUE_BUTTON}
          onClick={() => {
            window.location.href = `mailto:${UPGRADE_EMAIL}?subject=${encodeURIComponent('Upgrade to a paid seat')}`
          }}
        >
          Email {UPGRADE_EMAIL}
        </Button>
      )}
    </>
  )
}

export default AccountRows
